/**
 * PIACERSI — CURSORE MAGNETICO (+ LENTE PRODOTTO)
 * =============================================================================
 * Un anello che segue il puntatore con inerzia elastica e viene attratto dagli
 * elementi interattivi. Sopra l'immagine principale di un prodotto, LO STESSO
 * anello si trasforma in una lente d'ingrandimento: nessun secondo elemento.
 *
 *   a) l'anello insegue il puntatore per interpolazione lineare (lerp);
 *   b) gli elementi con `data-magnetic` si spostano VERSO il puntatore quando
 *      questo entra nel loro raggio;
 *   c) sull'immagine prodotto l'anello si espande in lente e mostra, nello
 *      pseudo-elemento ::before, la porzione sotto al mouse ingrandita (2x–3x),
 *      leggermente spostata a sinistra così il puntatore non la copre.
 *
 * REGOLE NON NEGOZIABILI
 *   · Il cursore di sistema NON viene mai nascosto.
 *   · L'anello è `pointer-events: none` e `aria-hidden`: non intercetta click
 *     e non esiste per uno screen reader. Essendo trasparente ai click,
 *     `elementFromPoint` legge sempre l'immagine sottostante, non l'anello.
 *   · Attivo solo con puntatore fine e senza `prefers-reduced-motion`.
 *     Su touch non viene nemmeno creato il nodo → la lente è disattivata.
 *
 * PERCHÉ È GPU/PERF-FRIENDLY
 *   Il JS scrive solo custom properties (--cx, --cy, --px, --py, --mx, --my, e per la
 *   lente --lens-*). Il movimento vive nel CSS come `translate3d`. Le geometrie
 *   (magneti e immagine della lente) sono lette a `resize`/`scroll` e all'entrata
 *   nella lente, MAI per frame: leggerle nel loop sarebbe layout thrashing.
 *   Un solo `requestAnimationFrame` per pagina, che si spegne a puntatore fermo.
 *
 * Registrato in `snippets/scripts.liquid`.
 * =============================================================================
 */

/** Raggio di attrazione oltre il bordo dell'elemento, in pixel. */
const PULL_RADIUS = 80;

/** Quota di distanza percorsa dall'elemento. Oltre 0.4 sembra un giocattolo. */
const PULL_RATIO = 0.25;

/**
 * Fattore di interpolazione dell'anello. Più basso = più inerzia.
 * Portato da 0,18 a 0,32: a 0,18 l'anello arrivava al bersaglio in ~22 frame,
 * cioè quasi quattro decimi di secondo, e con un anello largo 40px il ritardo
 * si leggeva come imprecisione — si "mirava" con un cerchio che stava ancora
 * viaggiando. A 0,32 sono ~10 frame: l'inerzia si vede ancora, ma l'anello è
 * addosso al puntatore. La precisione vera la dà comunque il punto centrale,
 * che non è interpolato affatto (vedi --px/--py qui sotto).
 */
const LERP = 0.32;

/** Soglia sotto la quale l'anello è considerato fermo e il loop si spegne. */
const SETTLE_PX = 0.5;

/* --- Lente ---------------------------------------------------------------- */

/**
 * Diametro della lente in pixel.
 *
 * Due tarature sbagliate prima di questa, in direzioni opposte:
 *   50px  — un cerchietto poco piu' grande dell'anello a riposo, che mostrava
 *           un francobollo dell'etichetta: faceva il contrario del suo mestiere;
 *   176px — troppo grande: su una colonna immagine da ~680px la lente ne
 *           copriva un quarto, quindi per guardare un dettaglio si perdeva di
 *           vista il prodotto intero — lo strumento nascondeva la cosa che
 *           deve mostrare;
 *   120px — ancora ingombrante.
 * 88px e' la misura finale: a 2,4x mostra una finestra di ~37px di sorgente,
 * cioe' il codice di una nuance o una riga corta di etichetta, e occupa il 13%
 * della colonna immagine. Lo zoom sale da 2,2 a 2,4 proprio perche' la finestra
 * si e' ristretta: meno superficie, ma piu' ingrandita, se no rimpicciolendo la
 * lente si perdeva anche la leggibilita' che e' il suo unico scopo.
 */
const LENS_DIAMETER = 88; // storico: ora il diametro e' proporzionato alla foto (vedi sotto)

/* 24/09/2026 — «LENTE DI INGRANDIMENTO DA INGRANDIRE» (richiesta dell'utente,
   con la schermata della Ristructa Maschera: il cerchio era ~90 px su una foto
   da ~600). La lente ora e' il 34% del lato della foto, fra 180 e 220 px: su
   una colonna da 560-680 px sono 190-220 px. Con lo zoom a 2,5 mostra una
   finestra di ~80 px di foto: un'etichetta intera, non una parola. */
const LENS_MIN = 180;
const LENS_MAX = 220;
const LENS_QUOTA = 0.34;

/** Fattore di ingrandimento della lente (compreso fra 2x e 3x). */
const LENS_ZOOM = 2.5;

/**
 * Spostamento della lente rispetto al puntatore, in pixel.
 *
 * Con la lente piccola serviva scostarla, se no il cursore di sistema la
 * copriva quasi tutta. A 176px il puntatore occupa il centro di un disco
 * grande e li' fa da mirino: lo scostamento diventa un difetto, perche' il
 * punto ingrandito non starebbe piu' sotto il dito. Vale anche una seconda
 * cosa: `#updateLensPosition` calcola la porzione sulla posizione NON
 * scostata, quindi con offset diverso da zero lente e porzione mostrata
 * andavano leggermente fuori registro. A zero, coincidono per costruzione.
 */
const LENS_OFFSET_X = 0;

/** Larghezza della sorgente ad alta risoluzione richiesta alla CDN Shopify. */
const HI_RES_WIDTH = 1600; // minimo; la larghezza vera si calcola sulla foto (#larghezzaNitida)

/** Contenitore di una singola media della gallery prodotto. */
const MEDIA_CONTAINER_SELECTOR = '.product-media-container';

class MagneticCursor {
  /** @type {HTMLElement | null} */
  #ring = null;

  /** @type {{ x: number, y: number }} */
  #target = { x: 0, y: 0 };

  /** @type {{ x: number, y: number }} */
  #current = { x: 0, y: 0 };

  /** @type {{ el: HTMLElement, box: DOMRect }[]} */
  #magnets = [];

  #running = false;

  /** Geometrie da rileggere al prossimo fotogramma (dopo uno scorrimento). */
  #geometrieVecchie = false;

  /* --- Stato della lente --- */
  /** @type {boolean} */
  #lensActive = false;
  /** @type {HTMLImageElement | null} */
  #lensImg = null;
  /** @type {DOMRect | null} */
  #lensRect = null;
  /** @type {number} Offset orizzontale corrente (interpolato). */
  #offsetX = 0;
  /** @type {number} Offset orizzontale bersaglio (0 o LENS_OFFSET_X). */
  #offsetXTarget = 0;
  /** Diametro corrente della lente, in pixel. */
  #lensD = LENS_MIN;

  constructor() {
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Nessun cursore (e nessuna lente) su touch o con movimento ridotto.
    if (!finePointer.matches || reducedMotion.matches) return;

    this.#ring = document.createElement('div');
    this.#ring.className = 'pcr-cursor';
    this.#ring.setAttribute('aria-hidden', 'true');
    document.body.appendChild(this.#ring);

    this.#target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    this.#current = { ...this.#target };

    this.#collectMagnets();

    document.addEventListener('pointermove', this.#onPointerMove, { passive: true });
    document.addEventListener('pointerleave', this.#onPointerLeave);
    window.addEventListener('resize', this.#collectMagnets, { passive: true });
    window.addEventListener('scroll', this.#onScroll, { passive: true });

    reducedMotion.addEventListener('change', (event) => {
      if (event.matches) this.destroy();
    });
  }

  destroy() {
    document.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerleave', this.#onPointerLeave);
    window.removeEventListener('resize', this.#collectMagnets);
    window.removeEventListener('scroll', this.#onScroll);

    for (const magnet of this.#magnets) {
      magnet.el.removeAttribute('data-pulling');
      magnet.el.style.removeProperty('--mx');
      magnet.el.style.removeProperty('--my');
    }
    this.#magnets = [];

    this.#exitLens();
    this.#ring?.remove();
    this.#ring = null;
  }

  /**
   * Legge le geometrie una volta sola (magneti + eventuale immagine lente).
   * Rileggerle dentro il loop costringerebbe a un reflow sincrono per frame.
   */
  #collectMagnets = () => {
    this.#magnets = [...document.querySelectorAll('[data-magnetic]')]
      .filter((node) => node instanceof HTMLElement)
      .map((el) => ({ el: /** @type {HTMLElement} */ (el), box: el.getBoundingClientRect() }));

    // Se la lente è attiva, la geometria dell'immagine può essere cambiata
    // (scroll/resize): la si rilegge qui, non nel loop.
    if (this.#lensActive) this.#refreshLensRect();
  };

  /* 23/09/2026 sera — LO SCORRIMENTO NON LEGGE PIU' LE GEOMETRIE.
     Prima ogni evento `scroll` chiamava `#collectMagnets`: querySelectorAll
     piu' un getBoundingClientRect per magnete, cioe' un'impaginazione forzata
     a ogni evento di scorrimento, su tutto il sito, anche col mouse fermo.
     Ora lo scorrimento segna soltanto «da rileggere»: la lettura avviene UNA
     volta, all'inizio del prossimo fotogramma del ciclo (prima di qualunque
     scrittura), e solo se il ciclo gira. Se la lente e' aperta il ciclo si
     riaccende, cosi' l'ingrandimento segue la foto anche scorrendo con la
     rotellina a mouse fermo. */
  #onScroll = () => {
    this.#geometrieVecchie = true;
    if (this.#lensActive && !this.#running) {
      this.#running = true;
      requestAnimationFrame(this.#tick);
    }
  };

  /**
   * Scrive un attributo solo se cambia: riassegnare lo stesso valore conta
   * comunque come una modifica per il motore di stile.
   * @param {HTMLElement} el
   * @param {string} chiave
   * @param {string} valore
   */
  #metti(el, chiave, valore) {
    if (el.dataset[chiave] !== valore) el.dataset[chiave] = valore;
  }

  #onPointerMove = (/** @type {PointerEvent} */ event) => {
    const ring = this.#ring;
    if (!ring) return;

    this.#target.x = event.clientX;
    this.#target.y = event.clientY;
    this.#metti(ring, 'active', 'true');

    // --px/--py (la posizione grezza per il punto centrale) non si scrivono
    // piu': il punto (::after) e' stato tolto il 07/08 e nessuno le leggeva.
    // Erano due invalidazioni di stile a ogni movimento del mouse.

    const under = document.elementFromPoint(event.clientX, event.clientY);
    this.#metti(ring, 'onDark', String(Boolean(under?.closest('.pcr-on-dark'))));

    // Lente: se il puntatore è sull'immagine principale del prodotto, lo stesso
    // anello diventa lente; altrimenti torna a comportarsi da cursore magnetico.
    const lensImg = this.#findLensImage(under);
    if (lensImg) {
      this.#enterLens(lensImg);
      // In modalità lente niente ingrossamento da magnete: comanda --lens-d.
      this.#metti(ring, 'magnet', 'false');
    } else {
      this.#exitLens();
      const interactive = Boolean(under?.closest('a, button, [data-magnetic], summary, [role="button"]'));
      this.#metti(ring, 'magnet', String(interactive));
    }

    if (!this.#running) {
      this.#running = true;
      requestAnimationFrame(this.#tick);
    }
  };

  #onPointerLeave = () => {
    if (this.#ring) this.#ring.dataset.active = 'false';
    this.#exitLens();
  };

  /* ==========================================================================
     LENTE
     ========================================================================== */

  /**
   * Immagine principale della gallery prodotto sotto il puntatore, o null.
   * Solo test sul DOM: nessuna lettura di layout.
   * @param {Element | null} under
   * @returns {HTMLImageElement | null}
   */
  #findLensImage(under) {
    if (!under) return null;
    const container = under.closest(MEDIA_CONTAINER_SELECTOR);
    if (!container) return null;
    // Deve trovarsi nella gallery principale della pagina prodotto. Funziona
    // sia in modalità "carousel" (slideshow) sia "grid": entrambe usano
    // `.product-media-container`, mentre `.media-gallery__grid` esiste solo in
    // grid — per questo NON lo si richiede.
    if (!container.closest('.product-information__media')) return null;
    // Esclude i video e le eventuali miniature.
    if (container.classList.contains('product-media-container--video')) return null;
    if (under.closest('[class*="thumbnail"]')) return null;
    return /** @type {HTMLImageElement | null} */ (container.querySelector('img'));
  }

  /**
   * Attiva o aggiorna la lente sull'immagine data. Idempotente sulla stessa
   * immagine: il setup pesante (sorgente hi-res, geometrie) avviene una volta.
   * @param {HTMLImageElement} img
   */
  #enterLens(img) {
    const ring = this.#ring;
    if (!ring) return;
    if (this.#lensActive && this.#lensImg === img) return;

    this.#lensImg = img;
    this.#lensActive = true;
    this.#offsetXTarget = LENS_OFFSET_X;

    // Sorgente ad alta risoluzione: si riscrive il parametro width della CDN.
    const src = img.currentSrc || img.src;
    const hiRes = this.#toHiRes(src, this.#larghezzaNitida(img));
    // Precarico per evitare il lampo al primo frame.
    if (hiRes) {
      const pre = new Image();
      pre.src = hiRes;
      // Due strati: finche' quella grande non e' arrivata si vede quella gia'
      // in pagina (sfocata ma presente), mai un disco vuoto.
      ring.style.setProperty('--lens-img', `url("${hiRes}"), url("${src}")`);
    }

    ring.dataset.lens = 'true';

    this.#refreshLensRect();
  }

  /** Disattiva la lente riportando l'anello al pallino, senza salti. */
  #exitLens() {
    const ring = this.#ring;
    if (!ring || !this.#lensActive) return;
    this.#lensActive = false;
    this.#lensImg = null;
    this.#lensRect = null;
    this.#offsetXTarget = 0;
    ring.dataset.lens = 'false';
    // --lens-img resta impostata ma lo ::before va a opacità 0 con transizione:
    // nessun salto. Verrà sovrascritta al prossimo ingresso.
  }

  /** Rilegge la geometria dell'immagine e ricalcola la dimensione dello sfondo. */
  #refreshLensRect() {
    const ring = this.#ring;
    if (!ring || !this.#lensImg) return;
    const rect = this.#lensImg.getBoundingClientRect();
    this.#lensRect = rect;
    const d = Math.round(Math.min(LENS_MAX, Math.max(LENS_MIN, Math.min(rect.width, rect.height) * LENS_QUOTA)));
    if (d !== this.#lensD || !ring.style.getPropertyValue('--lens-d')) {
      this.#lensD = d;
      ring.style.setProperty('--lens-d', `${d}px`);
    }
    const scaledW = rect.width * LENS_ZOOM;
    const scaledH = rect.height * LENS_ZOOM;
    ring.style.setProperty('--lens-size', `${scaledW.toFixed(1)}px ${scaledH.toFixed(1)}px`);
  }

  /**
   * Aggiorna la background-position dello ::before così che il punto sotto al
   * puntatore compaia al centro della lente. Chiamata nel loop: nessun accesso
   * al layout (usa la geometria in cache).
   */
  #updateLensPosition() {
    const ring = this.#ring;
    const rect = this.#lensRect;
    if (!ring || !rect || rect.width === 0) return;

    let fx = (this.#current.x - rect.left) / rect.width;
    let fy = (this.#current.y - rect.top) / rect.height;
    fx = fx < 0 ? 0 : fx > 1 ? 1 : fx;
    fy = fy < 0 ? 0 : fy > 1 ? 1 : fy;

    const scaledW = rect.width * LENS_ZOOM;
    const scaledH = rect.height * LENS_ZOOM;
    const posX = this.#lensD / 2 - fx * scaledW;
    const posY = this.#lensD / 2 - fy * scaledH;

    ring.style.setProperty('--lens-x', `${posX.toFixed(1)}px`);
    ring.style.setProperty('--lens-y', `${posY.toFixed(1)}px`);
  }

  /**
   * Riscrive l'URL Shopify per ottenere una versione ad alta risoluzione.
   * @param {string} url
   */
  #toHiRes(url, larghezza = HI_RES_WIDTH) {
    if (!url) return url;
    if (/[?&]width=\d+/.test(url)) {
      return url.replace(/([?&]width=)\d+/, `$1${larghezza}`);
    }
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + larghezza;
  }

  /**
   * Quanti pixel di sorgente servono perche' l'ingrandimento sia nitido:
   * lato mostrato x zoom x densita' dello schermo, arrotondato a 200 e fra
   * 1600 e 3200 (la CDN non ingrandisce oltre l'originale: chiedere di piu'
   * non costa niente, chiedere di meno sfoca).
   * @param {HTMLImageElement} img
   */
  #larghezzaNitida(img) {
    const lato = img.offsetWidth || 600;
    const serve = lato * LENS_ZOOM * Math.min(2, window.devicePixelRatio || 1);
    return Math.min(3200, Math.max(HI_RES_WIDTH, Math.ceil(serve / 200) * 200));
  }

  /* ==========================================================================
     LOOP
     ========================================================================== */

  #tick = () => {
    const ring = this.#ring;
    if (!ring) {
      this.#running = false;
      return;
    }

    // Le letture prima di ogni scrittura del fotogramma (vedi #onScroll).
    if (this.#geometrieVecchie) {
      this.#geometrieVecchie = false;
      this.#collectMagnets();
    }

    // Interpolazione verso il bersaglio: è questa a dare l'inerzia fluida.
    this.#current.x += (this.#target.x - this.#current.x) * LERP;
    this.#current.y += (this.#target.y - this.#current.y) * LERP;
    // Anche l'offset laterale della lente è interpolato: entra/esce senza salti.
    this.#offsetX += (this.#offsetXTarget - this.#offsetX) * LERP;

    ring.style.setProperty('--cx', `${(this.#current.x + this.#offsetX).toFixed(1)}px`);
    ring.style.setProperty('--cy', `${this.#current.y.toFixed(1)}px`);

    // La porzione ingrandita segue la posizione interpolata (coerente con la
    // lente): nessuna lettura di layout, solo aritmetica sulla geometria in cache.
    if (this.#lensActive && this.#lensRect) {
      this.#updateLensPosition();
    }

    for (const { el, box } of this.#magnets) {
      const cx = box.left + box.width / 2;
      const cy = box.top + box.height / 2;

      const inRange =
        this.#target.x > box.left - PULL_RADIUS &&
        this.#target.x < box.right + PULL_RADIUS &&
        this.#target.y > box.top - PULL_RADIUS &&
        this.#target.y < box.bottom + PULL_RADIUS;

      if (inRange) {
        el.dataset.pulling = 'true';
        el.style.setProperty('--mx', `${((this.#target.x - cx) * PULL_RATIO).toFixed(1)}px`);
        el.style.setProperty('--my', `${((this.#target.y - cy) * PULL_RATIO).toFixed(1)}px`);
      } else if (el.dataset.pulling) {
        delete el.dataset.pulling;
        el.style.setProperty('--mx', '0px');
        el.style.setProperty('--my', '0px');
      }
    }

    // Il loop si spegne quando anello E offset hanno raggiunto il bersaglio.
    const settled =
      Math.abs(this.#target.x - this.#current.x) < SETTLE_PX &&
      Math.abs(this.#target.y - this.#current.y) < SETTLE_PX &&
      Math.abs(this.#offsetXTarget - this.#offsetX) < SETTLE_PX;

    if (settled) {
      this.#running = false;
      return;
    }

    requestAnimationFrame(this.#tick);
  };
}

// Istanza unica per pagina. Il costruttore esce subito su touch e con
// movimento ridotto, quindi non serve alcun controllo qui.
if (!window.__pcrCursor) {
  const start = () => {
    window.__pcrCursor = new MagneticCursor();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}

export { MagneticCursor };
